import {
  Box,
  FormControl,
  FormHelperText,
  Typography,
  Select,
  MenuItem,
} from '@mui/material';
import { FC } from 'react';
import { capitalizeFirstLetter } from 'src/utils/text.utils';
import { AccountTypeProps } from 'src/models/FieldProps.interface';
import ExpandMoreOutlined from '@mui/icons-material/ExpandMoreOutlined';
import ErrorIcon from 'src/components/InputFields/ErrorIcon';

const AccountTypeField: FC<AccountTypeProps> = ({
  id,
  label,
  formikProps,
  sx,
  errorState = false,
  height = '40px',
  width = '450px',
}) => {
  const options = [
    { value: 'Current Account', label: 'Current Account' },
    { value: 'Saving Account', label: 'Saving Account' },
    { value: 'Fixed Deposit', label: 'Fixed Deposit' },
  ];

  return (
    <Box mb={3}>
      <Typography
        fontFamily="Outfit"
        fontWeight="400"
        fontSize="16px"
        color="var(--primary-navy)"
        mb={2}
      >
        {label}
      </Typography>
      <FormControl
        fullWidth
        error={
          formikProps.touched[id] &&
          (Boolean(formikProps.errors[id]) || errorState)
        }
        sx={{ m: 0 }}
      >
        <Select
          id={id}
          name={id}
          value={formikProps.values[id]}
          onChange={formikProps.handleChange}
          onBlur={formikProps.handleBlur}
          displayEmpty
          IconComponent={ExpandMoreOutlined}
          sx={{
            height: height,
            width: width,
            bgcolor: 'var(--text-bg)',
            border: 'none',
            borderRadius: 1,
            color: 'var(--primary-navy)',
            '& .MuiSelect-select': {
              padding: '10px 14px',
              fontFamily: 'Outfit',
              fontSize: '16px',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--border)',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--primary-blue)',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--primary-blue)',
            },
            '& input': {
              fontFamily: 'Outfit',
              fontSize: '16px',
            },
            overflow: 'hidden',
            '& input:-webkit-autofill': {
              bgcolor: 'var(--bg-color)',
              WebkitBoxShadow: '0 0 0 30px var(--bg-color) inset',
            },
            '& input:-moz-autofill': {
              bgcolor: 'var(--bg-color)',
              boxShadow: '0 0 0 30px var(--bg-color) inset',
            },
            '& input:-ms-autofill': {
              bgcolor: 'var(--bg-color)',
              boxShadow: '0 0 0 30px var(--bg-color) inset',
            },
            ...(formikProps.touched[id] &&
              Boolean(formikProps.errors[id]) && {
                borderColor: 'var(--error)',
              }),
            ...(errorState && {
              borderColor: 'var(--error)',
            }),
            ...sx,
          }}
          renderValue={(selected) =>
            selected
              ? capitalizeFirstLetter(selected as string)
              : `Select ${label}`
          }
          MenuProps={{
            PaperProps: {
              sx: {
                mt: 0.5,
                borderRadius: 2,
                border: '1px solid var(--border)',
                bgcolor: 'var(--text-bg)',
                boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
              },
            },
          }}
        >
          {options.map((option) => (
            <MenuItem
              key={option.value}
              value={option.value}
              sx={{
                fontFamily: 'Outfit',
                fontSize: '16px',
                fontWeight: 400,
                color: 'var(--primary-navy)',
                '&.Mui-selected': {
                  color: 'var(--primary-blue)',
                },
              }}
            >
              {option.label}
            </MenuItem>
          ))}
        </Select>
        {formikProps.touched[id] && formikProps.errors[id] && (
          <FormHelperText
            sx={{
              fontFamily: 'Outfit',
              fontWeight: '400',
              fontSize: '14px',
              color: 'var(--error)',
              display: 'flex',
              alignItems: 'center',
              ml: 0,
              mt: 1,
            }}
          >
            <ErrorIcon />
            {formikProps.errors[id]}
          </FormHelperText>
        )}
      </FormControl>
    </Box>
  );
};

export default AccountTypeField;
